import { differenceInMinutes, parseISO } from 'date-fns';
import type { Feature, Point, Polygon } from 'geojson';
import { parse, stringify } from 'wellknown';
import type { TrafikverketSourceOptions } from './Sources.types';
import { TrafikverketSource } from './TrafikverketSource';

const defaultOptions = (regionExtent: Polygon): TrafikverketSourceOptions => {
    const regionWkt = stringify(regionExtent);
    return {
        id: 'weather-station-source',
        objectType: 'WeatherMeasurepoint',
        schemaVersion: '2.1',
        namespace: 'Road.WeatherInfo',
        query: `
            <FILTER>
                <AND>
                    <WITHIN name="Geometry.WGS84" shape="polygon" value="${regionWkt}"/>
                </AND>
                <GT name='Observation.Sample' value='$dateadd(-7.00:00)' />
            </FILTER>
            <EXCLUDE>Observation.Aggregated5minutes</EXCLUDE>
            <EXCLUDE>Observation.Aggregated30minutes</EXCLUDE>
            <EXCLUDE>Geometry.SWEREF99TM</EXCLUDE>
        `,
        displayName: 'Väderstationer',
    };
};

interface TrafikverketWeatherMeasurepoint {
    id: string;
    name: string;
    geometry: {
        WGS84: string;
    };
    observation: {
        Sample: string;
        Air: {
            Temperature: {
                Value: number;
            };
            RelativeHumidity: {
                Value: number;
            };
        };
        Surface: {
            Temperature: {
                Value: number;
            };
        };
        Wind: {
            Direction: {
                Value: number;
            };
        };
        Weather: {
            Precipitation: string;
        };
        Aggregated10minutes: {
            Precipitation: {
                RainSum: {
                    Value: number;
                };
                SnowSum: {
                    Solid: {
                        Value: number;
                    };
                };
            };
            Wind: {
                SpeedAverage: {
                    Value: number;
                };
                SpeedMax: {
                    Value: number;
                };
            };
        };
    };
}

export interface WeatherMeasurepointProperties {
    name: string;
    isValid: boolean;
    iconId: string;
    precipitationType: string;
    roadTemp: number;
    airTemp: number;
    windSpeedAverage?: number;
    windSpeedMax?: number;
    windDirection: string;
    moisture: number;
    measureat: string;
}

export class WeatherMeasurepointSource extends TrafikverketSource {
    constructor(regionExtent: Polygon, options?: Partial<TrafikverketSourceOptions>) {
        super({ ...defaultOptions(regionExtent), ...options });
    }

    parseData(measurepoints: TrafikverketWeatherMeasurepoint[]): Feature<Point, WeatherMeasurepointProperties>[] {
        const remotePoints: { [key: string]: TrafikverketWeatherMeasurepoint } = {};

        function getWindDirectionText(value: number): string {
            if (value >= 337.5 || value < 22.5) {
                return 'Nordlig';
            }
            if (value < 67.5) {
                return 'Nordostlig';
            }
            if (value < 112.5) {
                return 'Östlig';
            }
            if (value < 157.5) {
                return 'Sydöslig';
            }
            if (value < 202.5) {
                return 'Sydlig';
            }
            if (value < 247.5) {
                return 'Sydvästlig';
            }
            if (value < 292.5) {
                return 'Västlig';
            }
            return 'Nordvästlig';
        }
        function getPrecipitationType(value: string, rainSum: number, snowSum: number): string {
            switch (value) {
                case 'no':
                    return 'Ingen nederbörd';
                case 'rain':
                    return getPercipitationText('Regn', rainSum);
                case 'freezing_rain':
                    return 'Underkylt regn';
                case 'snow':
                    return getPercipitationText('Snöfall', snowSum);
                case 'sleet':
                    return getPercipitationText('Snöblandat regn', rainSum);
                case 'hail':
                    return 'Annan nederbördstyp';
                case 'drizzle':
                    return 'Annan nederbördstyp';
                default:
                    return 'Okänd nederbördstyp';
            }
        }

        function getPercipitationText(type: string, sum: number): string {
            if (sum > 0 && sum < 0.5) return `Lätt ${type.toLocaleLowerCase()}`;
            if (sum < 4) return `Måttligt ${type.toLocaleLowerCase()}`;
            if (sum >= 4) return `Kraftigt ${type.toLocaleLowerCase()}`;
            return type;
        }

        function getIconId(isValid: boolean, value: string): string {
            if (!isValid) return 'precipitationNoData';

            switch (value) {
                case 'no':
                    return 'precipitationNoPrecipitation';
                case 'rain':
                    return 'precipitationRain';
                case 'freezing_rain':
                    return 'precipitationFreezingRain';
                case 'snow':
                    return 'precipitationSnow';
                case 'sleet':
                    return 'precipitationSleet';
                case 'hail':
                    return 'precipitationSnow';
                default:
                    return 'precipitationNoPrecipitation';
            }
        }

        return measurepoints
            .filter(function (mp) {
                if (/^[^\d]*?[\d]{2,4}100$/g.test(mp.id)) {
                    remotePoints[mp.id.slice(0, -3)] = mp;
                    return false;
                }
                return true;
            })
            .map(function (mp) {
                const geoJSON = mp.geometry?.WGS84 && (parse(mp.geometry.WGS84) as Point);
                const remotePoint = remotePoints[mp.id];
                const windDirection = mp.observation.Wind[0]?.Direction?.Value;
                const isValid = mp.observation?.Sample && differenceInMinutes(Date(), parseISO(mp.observation?.Sample)) < 30;

                return {
                    id: mp.id,
                    type: 'Feature',
                    geometry: geoJSON,
                    properties: {
                        name: mp.name,
                        isValid: isValid,
                        iconId: getIconId(isValid, mp.observation?.Weather?.Precipitation),
                        precipitationType: getPrecipitationType(
                            mp.observation?.Weather?.Precipitation,
                            mp.observation?.Aggregated10minutes?.Precipitation?.RainSum?.Value,
                            mp.observation?.Aggregated10minutes?.Precipitation?.SnowSum?.Solid?.Value,
                        ),
                        roadTemp: (remotePoint ?? mp).observation?.Surface?.Temperature?.Value,
                        airTemp: (remotePoint ?? mp).observation?.Air?.Temperature?.Value,
                        windSpeedMax: mp.observation?.Aggregated10minutes?.Wind?.SpeedMax?.Value ?? 0,
                        windSpeedAverage: mp.observation?.Aggregated10minutes?.Wind?.SpeedAverage?.Value,
                        windDirection: windDirection && getWindDirectionText(windDirection),
                        moisture: mp.observation?.Air?.RelativeHumidity?.Value,
                        measureat: mp.observation?.Sample,
                    },
                };
            });
    }
}
